import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconCalendar,
  IconDownload,
  IconInfoCircle,
  IconRefresh,
  IconSearch,
  IconShieldHalf,
  IconX,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchList } from '@/shared/api/jsonapi/client';
import { parseBeIso } from '@/features/attendance/api';
import { EmptyState } from '@/shared/components/EmptyState';
import { notifications } from '@mantine/notifications';
import { downloadCsv, type CsvColumn } from '@/shared/lib/exportCsv';
import type { ImpersonationLog } from '@/shared/types/entities';

export const Route = createFileRoute('/_app/admin/audit-log')({ component: AuditLogPage });

const PAGE_SIZE = 25;

const CSV_COLUMNS: Array<CsvColumn<ImpersonationLog>> = [
  { header: 'Inizio', accessor: (r) => r.startedAt },
  { header: 'Fine', accessor: (r) => r.endedAt },
  { header: 'Attore', accessor: (r) => r.actorUsername ?? r.actorUser?.id },
  { header: 'Target', accessor: (r) => r.targetUsername ?? r.targetUser?.id },
  { header: 'Tenant', accessor: (r) => r.targetTenant?.name ?? r.targetTenant?.id },
  { header: 'IP', accessor: (r) => r.ip },
];

function AuditLogPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 200);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [page, setPage] = useState(1);

  const listQ = useQuery({
    queryKey: ['impersonation-logs-all'],
    queryFn: () => fetchList<ImpersonationLog>('impersonation-logs', {
      sort: ['-startedAt'],
      page: { size: 500 },
    }),
  });

  const all = useMemo(() => listQ.data?.data ?? [], [listQ.data]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const [from, to] = range;
    return all.filter((row) => {
      if (q) {
        const hay = `${row.actorUsername ?? ''} ${row.targetUsername ?? ''} ${row.targetTenant?.name ?? ''} ${row.ip ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (from && row.startedAt && parseBeIso(row.startedAt) < from) return false;
      if (to && row.startedAt && parseBeIso(row.startedAt) > dayjs(to).endOf('day').toDate()) return false;
      return true;
    });
  }, [all, debouncedSearch, range]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageData = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeCount = useMemo(() => all.filter((r) => !r.endedAt).length, [all]);

  // Esporta le righe correnti caricate (già filtrate client-side).
  const exportCsv = () => {
    const rows = filtered;
    if (rows.length === 0) {
      notifications.show({ color: 'orange', message: 'Nessun dato da esportare' });
      return;
    }
    downloadCsv(`audit-log-${dayjs().format('YYYYMMDD-HHmm')}`, CSV_COLUMNS, rows);
    notifications.show({ color: 'green', message: `${rows.length} righe esportate` });
  };

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap" align="flex-end">
        <Group>
          <IconShieldHalf size={28} />
          <div>
            <Title order={1}>Audit log impersonificazioni</Title>
            <Text size="sm" c="dimmed">
              {all.length} sessioni totali · {activeCount} ancora attive
            </Text>
          </div>
        </Group>
        <Group gap="xs">
          <Tooltip label="Aggiorna">
            <ActionIcon
              variant="light"
              size="lg"
              aria-label="Aggiorna lista impersonificazioni"
              onClick={() => qc.invalidateQueries({ queryKey: ['impersonation-logs-all'] })}
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconDownload size={14} />}
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            Esporta CSV
          </Button>
        </Group>
      </Group>

      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Storico delle sessioni di impersonificazione SuperAdmin. Solo lettura. Le sessioni con
        <code> endedAt</code> vuoto sono ancora attive.
      </Alert>

      <Card withBorder padding="md" radius="md">
        <Group wrap="wrap" gap="sm">
          <TextInput
            placeholder="Cerca actor, target, tenant o IP…"
            leftSection={<IconSearch size={14} />}
            rightSection={search ? (
              <ActionIcon variant="subtle" size="sm" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                <IconX size={12} />
              </ActionIcon>
            ) : null}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={300}
          />
          <DatePickerInput
            type="range"
            placeholder="Periodo"
            value={range}
            onChange={(v) => setRange(v as [Date | null, Date | null])}
            leftSection={<IconCalendar size={14} />}
            clearable
            w={260}
            valueFormat="DD/MM/YYYY"
          />
          {(search || range[0] || range[1]) && (
            <Button
              variant="subtle"
              size="xs"
              onClick={() => { setSearch(''); setRange([null, null]); }}
            >
              Pulisci filtri
            </Button>
          )}
          {filtered.length !== all.length && (
            <Badge variant="light" color="blue">
              {filtered.length} di {all.length}
            </Badge>
          )}
        </Group>
      </Card>

      {listQ.isLoading ? (
        <Center py="xl"><Loader /></Center>
      ) : filtered.length === 0 ? (
        <Paper withBorder radius="md">
          <EmptyState
            icon={IconShieldHalf}
            title={all.length === 0 ? 'Nessuna sessione di impersonificazione registrata' : 'Nessun risultato per i filtri correnti'}
            description={all.length === 0
              ? 'Le sessioni di impersonificazione SuperAdmin compariranno qui appena avviate.'
              : 'Prova ad allargare il periodo o pulire la ricerca.'}
          />
        </Paper>
      ) : (
        <>
          <Card withBorder padding={0} radius="md">
            <ScrollArea type="auto">
              <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Inizio</Table.Th>
                    <Table.Th>Fine</Table.Th>
                    <Table.Th>Durata</Table.Th>
                    <Table.Th>Attore</Table.Th>
                    <Table.Th>Target</Table.Th>
                    <Table.Th>Tenant</Table.Th>
                    <Table.Th>IP</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageData.map((row) => {
                    const start = row.startedAt ? parseBeIso(row.startedAt) : null;
                    const end = row.endedAt ? parseBeIso(row.endedAt) : null;
                    const durMin = start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;
                    return (
                      <Table.Tr key={row.id}>
                        <Table.Td>
                          <Text size="sm">{start?.toLocaleString('it-IT') ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          {end
                            ? <Text size="sm">{end.toLocaleString('it-IT')}</Text>
                            : <Badge color="orange" variant="light">Attiva</Badge>}
                        </Table.Td>
                        <Table.Td>
                          {durMin != null
                            ? <Text size="xs" c="dimmed">{durMin < 60 ? `${durMin} min` : `${(durMin / 60).toFixed(1)} h`}</Text>
                            : <Text size="xs" c="dimmed">—</Text>}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={600}>{row.actorUsername ?? row.actorUser?.id ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{row.targetUsername ?? row.targetUser?.id ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">{row.targetTenant?.name ?? row.targetTenant?.id ?? '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>{row.ip ?? '—'}</Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>
          {totalPages > 1 && (
            <Group justify="center">
              <Pagination
                total={totalPages}
                value={safePage}
                onChange={setPage}
                size="sm"
              />
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}
